import { useState } from "react";
import {
  useGetAnalyticsSummary, useGetPatterns, useListStudents,
  useGetAdjustmentAnalytics,
} from "@workspace/api-client-react";
import {
  BarChart3, TrendingUp, AlertTriangle, Users, FileText, Calendar,
  ChevronDown, ChevronRight, BookMarked, Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { format, subDays } from "date-fns";
import {
  formatStudentName,
  useDefaultStudentDisplayMode,
  type Student as DisplayStudent,
} from "@/lib/studentDisplay";
import { ADJUSTMENT_MAP, NCCD_CATEGORIES } from "@/lib/nccd-adjustments";

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  nccd: "bg-blue-500",
  contact: "bg-green-500",
  behaviour: "bg-purple-500",
  general: "bg-gray-400",
};

const TYPE_LABELS: Record<string, string> = {
  nccd: "NCCD",
  contact: "Contact",
  behaviour: "Behaviour",
  general: "General",
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  "Instructional":          { bg: "bg-blue-50",   text: "text-blue-700",   bar: "bg-blue-500"   },
  "Assessment":             { bg: "bg-violet-50",  text: "text-violet-700", bar: "bg-violet-500" },
  "Environmental":          { bg: "bg-green-50",   text: "text-green-700",  bar: "bg-green-500"  },
  "Curriculum":             { bg: "bg-orange-50",  text: "text-orange-700", bar: "bg-orange-500" },
  "Social & Emotional":     { bg: "bg-pink-50",    text: "text-pink-700",   bar: "bg-pink-500"   },
  "Technology & Equipment": { bg: "bg-teal-50",    text: "text-teal-700",   bar: "bg-teal-500"   },
};

function frequencyLabel(count: number, max: number) {
  if (count >= 3) return { label: "Consistent", classes: "bg-blue-100 text-blue-700 border-blue-200" };
  if (count === 2) return { label: "Occasional", classes: "bg-amber-100 text-amber-700 border-amber-200" };
  return { label: "One-off", classes: "bg-gray-100 text-gray-500 border-gray-200" };
}

// ─── Overall adjustments tab ──────────────────────────────────────────────────

function OverallAdjustmentsTab({ data }: { data: { id: string; count: number }[] }) {
  const [showAll, setShowAll] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>("all");

  const enriched = data
    .map((item) => {
      const adj = ADJUSTMENT_MAP.get(item.id);
      return { ...item, label: adj?.label ?? item.id, category: adj?.category ?? "Other" };
    })
    .filter((item) => filterCategory === "all" || item.category === filterCategory);

  const displayed = showAll ? enriched : enriched.slice(0, 15);
  const maxCount = Math.max(...enriched.map((e) => e.count), 1);

  if (data.length === 0) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm">
        No NCCD records with adjustments logged yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Category filter pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilterCategory("all")}
          className={`text-xs px-3 py-1 rounded-full border transition-colors ${filterCategory === "all" ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}
        >
          All categories
        </button>
        {NCCD_CATEGORIES.map((cat) => {
          const c = CATEGORY_COLORS[cat];
          return (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${filterCategory === cat ? `${c.bar} text-white border-transparent` : `border-gray-200 text-gray-600 hover:border-gray-400`}`}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* Ranked list */}
      <div className="space-y-2">
        {displayed.map((item, i) => {
          const c = CATEGORY_COLORS[item.category] ?? { bg: "bg-gray-50", text: "text-gray-600", bar: "bg-gray-400" };
          const pct = Math.round((item.count / maxCount) * 100);
          return (
            <div key={item.id} className="flex items-center gap-3 group">
              <span className="text-xs text-gray-300 w-5 text-right flex-shrink-0">#{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800 truncate">{item.label}</span>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${c.bg} ${c.text}`}>
                      {item.category}
                    </span>
                    <span className="text-sm font-semibold text-gray-700 tabular-nums">{item.count}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${c.bar}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {enriched.length > 15 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        >
          {showAll ? "Show less" : `Show all ${enriched.length} adjustments`}
          <ChevronDown className={`h-3 w-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}

// ─── By Student tab ───────────────────────────────────────────────────────────

interface StudentRow {
  studentId: number;
  studentName: string;
  nccdRecordCount: number;
  adjustments: { id: string; count: number }[];
}

function ByStudentTab({ students }: { students: StudentRow[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  if (students.length === 0) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm">
        No student adjustment data available yet.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Legend */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
        <span className="text-xs text-gray-500 font-medium">Frequency:</span>
        {[
          { label: "Consistent (3+ records)", classes: "bg-blue-100 text-blue-700 border-blue-200" },
          { label: "Occasional (2 records)", classes: "bg-amber-100 text-amber-700 border-amber-200" },
          { label: "One-off (1 record)", classes: "bg-gray-100 text-gray-500 border-gray-200" },
        ].map((f) => (
          <span key={f.label} className={`text-xs px-2 py-0.5 rounded-full border ${f.classes}`}>{f.label}</span>
        ))}
      </div>

      {students.map((s) => {
        const isOpen = expanded === s.studentId;
        const maxCount = Math.max(...s.adjustments.map((a) => a.count), 1);
        const consistentCount = s.adjustments.filter((a) => a.count >= 3).length;
        const topAdj = s.adjustments.slice(0, 3);

        return (
          <div key={s.studentId} className="border border-gray-100 rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              onClick={() => setExpanded(isOpen ? null : s.studentId)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-900">{s.studentName}</span>
                  <Badge variant="secondary" className="text-xs h-5 px-1.5">
                    {s.nccdRecordCount} NCCD {s.nccdRecordCount === 1 ? "record" : "records"}
                  </Badge>
                  {consistentCount > 0 && (
                    <span className="text-xs text-blue-600 font-medium">
                      {consistentCount} consistent adjustment{consistentCount !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                {!isOpen && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {topAdj.map((a) => {
                      const label = ADJUSTMENT_MAP.get(a.id)?.label ?? a.id;
                      const { classes } = frequencyLabel(a.count, maxCount);
                      return (
                        <span key={a.id} className={`text-xs px-1.5 py-0.5 rounded border ${classes}`}>
                          {label}
                        </span>
                      );
                    })}
                    {s.adjustments.length > 3 && (
                      <span className="text-xs text-gray-400">+{s.adjustments.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
              <ChevronRight className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
            </button>

            {isOpen && (
              <div className="px-4 pb-4 border-t border-gray-50 bg-gray-50/40">
                <div className="pt-3 space-y-2">
                  {s.adjustments.map((a) => {
                    const adj = ADJUSTMENT_MAP.get(a.id);
                    const label = adj?.label ?? a.id;
                    const cat = adj?.category ?? "Other";
                    const { label: freqLabel, classes: freqClasses } = frequencyLabel(a.count, maxCount);
                    const c = CATEGORY_COLORS[cat] ?? { bg: "bg-gray-50", text: "text-gray-600", bar: "bg-gray-400" };
                    const pct = Math.round((a.count / maxCount) * 100);
                    return (
                      <div key={a.id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-medium text-gray-800 truncate">{label}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}>{cat}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium ${freqClasses}`}>
                                {freqLabel} · {a.count}×
                              </span>
                            </div>
                          </div>
                          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── By Class tab ─────────────────────────────────────────────────────────────

interface ClassRow {
  classId: number;
  className: string;
  nccdRecordCount: number;
  adjustments: { id: string; count: number }[];
}

function ByClassTab({ classes }: { classes: ClassRow[] }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  if (classes.length === 0) {
    return (
      <div className="py-10 text-center text-gray-400 text-sm">
        No class adjustment data available. Make sure records are linked to classes.
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Category key */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        {Object.entries(CATEGORY_COLORS).map(([cat, c]) => (
          <span key={cat} className={`text-xs px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{cat}</span>
        ))}
      </div>

      {classes.map((cls) => {
        const isOpen = expanded === cls.classId;
        const maxCount = Math.max(...cls.adjustments.map((a) => a.count), 1);
        const top5 = cls.adjustments.slice(0, 5);

        return (
          <div key={cls.classId} className="border border-gray-100 rounded-lg overflow-hidden">
            <button
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
              onClick={() => setExpanded(isOpen ? null : cls.classId)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-gray-900">{cls.className}</span>
                  <Badge variant="secondary" className="text-xs h-5 px-1.5">
                    {cls.nccdRecordCount} adjustments logged
                  </Badge>
                </div>
                {!isOpen && (
                  <div className="flex gap-1 flex-wrap">
                    {top5.map((a) => {
                      const adj = ADJUSTMENT_MAP.get(a.id);
                      const cat = adj?.category ?? "Other";
                      const c = CATEGORY_COLORS[cat] ?? { bg: "bg-gray-50", text: "text-gray-600", bar: "bg-gray-400" };
                      return (
                        <span key={a.id} className={`text-xs px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}>
                          {adj?.label ?? a.id} ({a.count})
                        </span>
                      );
                    })}
                    {cls.adjustments.length > 5 && (
                      <span className="text-xs text-gray-400">+{cls.adjustments.length - 5} more</span>
                    )}
                  </div>
                )}
              </div>
              <ChevronRight className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
            </button>

            {isOpen && (
              <div className="px-4 pb-4 border-t border-gray-50 bg-gray-50/40">
                <div className="pt-3 space-y-2">
                  {cls.adjustments.map((a) => {
                    const adj = ADJUSTMENT_MAP.get(a.id);
                    const label = adj?.label ?? a.id;
                    const cat = adj?.category ?? "Other";
                    const c = CATEGORY_COLORS[cat] ?? { bg: "bg-gray-50", text: "text-gray-600", bar: "bg-gray-400" };
                    const pct = Math.round((a.count / maxCount) * 100);
                    return (
                      <div key={a.id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-medium text-gray-800 truncate">{label}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${c.bg} ${c.text}`}>{cat}</span>
                              <span className="text-xs font-semibold text-gray-600 tabular-nums">{a.count}×</span>
                            </div>
                          </div>
                          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Analytics page ──────────────────────────────────────────────────────

export default function Analytics() {
  const { data: summary, isLoading: isSummaryLoading } = useGetAnalyticsSummary();
  const { data: patterns } = useGetPatterns();
  const { data: students } = useListStudents();
  const { data: adjData, isLoading: isAdjLoading } = useGetAdjustmentAnalytics();

  const accountDisplayMode = useDefaultStudentDisplayMode();
  const studentMap = new Map(
    students?.map((s) => [s.id, formatStudentName(s as DisplayStudent, accountDisplayMode)]),
  );

  if (isSummaryLoading) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <BarChart3 className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold text-gray-900">Analytics & Patterns</h1>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Records</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{summary?.totalRecords ?? 0}</p>
              </div>
              <FileText className="h-8 w-8 text-gray-200" />
            </div>
          </CardContent>
        </Card>


        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Students Documented</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{summary?.studentsWithRecords ?? 0}</p>
              </div>
              <Users className="h-8 w-8 text-gray-200" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">This Week</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{summary?.recordsThisWeek ?? 0}</p>
              </div>
              <Calendar className="h-8 w-8 text-green-100" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {summary?.recordsByType && Object.keys(summary.recordsByType).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Records by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(summary.recordsByType as Record<string, number>)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => {
                    const total = summary.totalRecords || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={type}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">{TYPE_LABELS[type] ?? type}</span>
                          <span className="text-sm text-gray-500">{count} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${TYPE_COLORS[type] ?? "bg-gray-400"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {summary?.topStudentsByRecordCount && summary.topStudentsByRecordCount.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Most Documented Students</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {summary.topStudentsByRecordCount.map((item: { studentId: number; studentName: string; count: number }, i: number) => (
                  <div key={item.studentId} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                    <span className="text-lg font-bold text-gray-300 w-6 text-right">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.studentName}</p>
                    </div>
                    <Badge variant="secondary" className="text-xs">{item.count} records</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── NCCD Adjustment Analysis ─────────────────────────────────────────── */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BookMarked className="h-4 w-4 text-purple-600" />
                NCCD Adjustment Analysis
              </CardTitle>
              <p className="text-sm text-gray-500 mt-0.5">
                {adjData
                  ? `Based on ${adjData.totalNccdRecords} NCCD records · ${adjData.overallAdjustments.length} distinct adjustment types used`
                  : "Analysing adjustment patterns…"}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isAdjLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-gray-100 animate-pulse rounded" />)}
            </div>
          ) : adjData?.totalNccdRecords === 0 ? (
            <div className="py-10 text-center border-2 border-dashed border-gray-100 rounded-xl">
              <BookMarked className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-500">No NCCD records yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Create NCCD records with adjustments to see patterns here.
              </p>
            </div>
          ) : (
            <Tabs defaultValue="overall">
              <TabsList className="mb-4">
                <TabsTrigger value="overall">Overall</TabsTrigger>
                <TabsTrigger value="student">
                  By Student
                  {adjData && adjData.byStudent.length > 0 && (
                    <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                      {adjData.byStudent.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="class">
                  By Class
                  {adjData && adjData.byClass.length > 0 && (
                    <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                      {adjData.byClass.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overall">
                <OverallAdjustmentsTab data={adjData?.overallAdjustments ?? []} />
              </TabsContent>

              <TabsContent value="student">
                <ByStudentTab students={adjData?.byStudent ?? []} />
              </TabsContent>

              <TabsContent value="class">
                <ByClassTab classes={adjData?.byClass ?? []} />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      {/* ── Incidents by Period ──────────────────────────────────────────────── */}
      {patterns?.byPeriod && patterns.byPeriod.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Incidents by Period</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {(patterns.byPeriod as { period: string; count: number }[]).map((item) => {
                const max = Math.max(...(patterns.byPeriod as { count: number }[]).map((b) => b.count));
                const height = max > 0 ? Math.round((item.count / max) * 100) : 0;
                return (
                  <div key={item.period} className="flex flex-col items-center flex-1 gap-1">
                    <span className="text-xs text-gray-500">{item.count}</span>
                    <div
                      className="w-full bg-primary/80 rounded-t-md"
                      style={{ height: `${height}%`, minHeight: "4px" }}
                      title={`Period ${item.period}: ${item.count} incidents`}
                    />
                    <span className="text-xs text-gray-600 font-medium">{item.period}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Recent Records ───────────────────────────────────────────────────── */}
      {summary?.recentRecords && summary.recentRecords.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Recent Records</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(summary.recentRecords as { id: number; type: string; date: string; description: string | null; studentNames: string[] }[]).map((record) => (
                <div key={record.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${TYPE_COLORS[record.type] ?? "bg-gray-400"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">{record.description ?? "No description"}</p>
                    <p className="text-xs text-gray-500">
                      {format(new Date(record.date), "d MMM yyyy")}
                      {record.studentNames?.length > 0 && ` · ${record.studentNames[0]}`}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">{TYPE_LABELS[record.type] ?? record.type}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {summary?.totalRecords === 0 && (
        <div className="py-24 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No data yet</h3>
          <p className="text-gray-500">Start creating records to see analytics and patterns here.</p>
        </div>
      )}
    </div>
  );
}
